import { useCallback, useEffect, useRef, useState } from 'react';
import { api, ApiError, uploadThumbnail, uploadVideoFile } from '../api.ts';
import { Card, Field, Tag, useToast } from '../ui.tsx';
import { fmtBytes, fmtDuration } from '../format.ts';

/**
 * Painel de mídia.
 * O arquivo passa por três checagens antes de virar origem do vídeo:
 * 1) tipo declarado + extensão; 2) reprodução real no <video> local
 * (canplay + videoWidth/videoHeight + duration); 3) validação de container e
 * codec no servidor. Só depois disso a publicação é liberada.
 */

const ACCEPTED = ['video/mp4', 'video/webm', 'video/quicktime', 'video/x-m4v'];
const ACCEPT_ATTR = '.mp4,.webm,.mov,.m4v,video/mp4,video/webm,video/quicktime';

interface CodecIssue { code: string; severity: 'error' | 'warning' | 'info'; message: string; hint?: string }
interface CodecReport {
  container: string; videoCodec: string | null; audioCodec: string | null;
  videoCodecLabel: string | null; audioCodecLabel: string | null;
  width: number | null; height: number | null; durationSec: number | null;
  mimeType: string; fullMimeType: string; browserSupport: string; issues: CodecIssue[]; fastStart: boolean | null;
}
export interface AssetInfo {
  id: string; origin_kind: string; original_filename: string; declared_mime: string; detected_mime: string;
  size_bytes: number; visibility: string; codec_report: CodecReport | null; external_url: string | null;
  storage_ref: string | null; validated_at: string | null;
}

interface LocalCheck {
  state: 'idle' | 'checking' | 'ok' | 'failed';
  message: string;
  detail?: string;
  width?: number;
  height?: number;
  duration?: number;
  canPlayType?: string;
}

export function VideoMediaPanel({
  videoId, workspaceId, asset, status, canWrite, onChanged,
}: {
  videoId: string;
  workspaceId: string;
  asset: AssetInfo | null;
  status: string;
  canWrite: boolean;
  onChanged: () => void;
}): JSX.Element {
  const toast = useToast();
  const inputRef = useRef<HTMLInputElement>(null);
  const previewRef = useRef<HTMLVideoElement>(null);
  const thumbInputRef = useRef<HTMLInputElement>(null);

  const [file, setFile] = useState<File | null>(null);
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [check, setCheck] = useState<LocalCheck>({ state: 'idle', message: '' });
  const [progress, setProgress] = useState<number | null>(null);
  const [serverReport, setServerReport] = useState<CodecReport | null>(null);
  const [dragging, setDragging] = useState(false);
  const [externalUrl, setExternalUrl] = useState('');
  const [urlCheck, setUrlCheck] = useState<any>(null);
  const [urlBusy, setUrlBusy] = useState(false);
  const abortRef = useRef<(() => void) | null>(null);

  useEffect(() => () => { if (objectUrl) URL.revokeObjectURL(objectUrl); }, [objectUrl]);

  /** Validação local: o navegador precisa realmente abrir e conseguir tocar. */
  const validateLocally = useCallback((candidate: File): void => {
    setServerReport(null);
    const extensionOk = /\.(mp4|webm|mov|m4v)$/i.test(candidate.name);
    const mimeOk = ACCEPTED.includes(candidate.type) || candidate.type === '';

    if (!extensionOk && !ACCEPTED.includes(candidate.type)) {
      setCheck({
        state: 'failed',
        message: `"${candidate.name}" não tem extensão nem tipo de vídeo aceito.`,
        detail: `tipo informado pelo navegador: ${candidate.type || '(vazio)'}`,
      });
      return;
    }
    if (!mimeOk) {
      setCheck({
        state: 'failed',
        message: `O tipo "${candidate.type}" não é um container de vídeo aceito.`,
        detail: 'Aceitos: MP4 (H.264/AAC), WebM, MOV e M4V.',
      });
      return;
    }
    if (candidate.size === 0) {
      setCheck({ state: 'failed', message: 'O arquivo está vazio (0 byte).' });
      return;
    }

    const url = URL.createObjectURL(candidate);
    setObjectUrl((previous) => {
      if (previous) URL.revokeObjectURL(previous);
      return url;
    });

    const probe = document.createElement('video');
    const canPlay = candidate.type ? probe.canPlayType(candidate.type) : '';
    setCheck({ state: 'checking', message: 'Abrindo o arquivo no navegador…', canPlayType: canPlay || 'desconhecido' });

    probe.preload = 'metadata';
    probe.muted = true;
    probe.playsInline = true;
    probe.src = url;

    let settled = false;
    const finish = (result: LocalCheck): void => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timeout);
      setCheck(result);
      probe.removeAttribute('src');
      probe.load();
    };

    const timeout = window.setTimeout(() => {
      finish({
        state: 'failed',
        message: 'O navegador não conseguiu abrir a mídia dentro do tempo esperado.',
        detail: 'Isso costuma indicar codec incompatível (HEVC/H.265, ProRes) ou arquivo corrompido.',
      });
    }, 15000);

    probe.addEventListener('loadedmetadata', () => {
      if (!probe.videoWidth || !probe.videoHeight) {
        finish({
          state: 'failed',
          message: 'O arquivo abriu, mas sem imagem: videoWidth/videoHeight = 0.',
          detail: 'Codec de vídeo não suportado pelo navegador. Converta para MP4 H.264 + AAC.',
        });
        return;
      }
      if (!Number.isFinite(probe.duration) || probe.duration <= 0) {
        finish({ state: 'failed', message: 'Duração inválida — o arquivo pode estar incompleto ou corrompido.' });
      }
    });

    probe.addEventListener('canplay', () => {
      finish({
        state: 'ok',
        message: 'O navegador abriu e conseguiu iniciar a reprodução deste arquivo.',
        width: probe.videoWidth,
        height: probe.videoHeight,
        duration: probe.duration,
        canPlayType: canPlay || 'desconhecido',
      });
    });

    probe.addEventListener('error', () => {
      const code = probe.error?.code ?? 0;
      const detail: Record<number, string> = {
        1: 'Carregamento abortado.',
        2: 'Erro de rede ao ler o arquivo.',
        3: 'Falha ao decodificar: o codec não é suportado.',
        4: 'Formato/codec não suportado por este navegador.',
      };
      finish({
        state: 'failed',
        message: 'O navegador não conseguiu abrir esta mídia.',
        detail: `${detail[code] ?? 'Erro desconhecido.'} (MediaError ${code}) — converta para MP4 H.264 + AAC.`,
      });
    });
  }, []);

  function pick(candidate: File | null | undefined): void {
    if (!candidate) return;
    setFile(candidate);
    setProgress(null);
    validateLocally(candidate);
  }

  async function upload(): Promise<void> {
    if (!file || check.state !== 'ok') return;
    setProgress(0);
    const { promise, abort } = uploadVideoFile(videoId, file, workspaceId, (p) => setProgress(p.percent));
    abortRef.current = abort;
    try {
      const result = await promise;
      setProgress(100);
      setServerReport(result.report as CodecReport);
      if (result.publishable) toast('Arquivo validado e pronto para publicar.', 'ok');
      else toast('Arquivo enviado, mas reprovado na validação de codec.', 'error');
      onChanged();
    } catch (err) {
      toast(err instanceof ApiError ? err.message : 'Falha no upload.', 'error');
      setProgress(null);
    } finally {
      abortRef.current = null;
    }
  }

  async function checkUrl(): Promise<void> {
    setUrlBusy(true);
    setUrlCheck(null);
    try {
      const result = await api('/api/media/check-url', { body: { url: externalUrl } });
      setUrlCheck(result);
    } catch (err) {
      setUrlCheck({ ok: false, reason: err instanceof ApiError ? err.message : 'Falha ao verificar.' });
    } finally {
      setUrlBusy(false);
    }
  }

  async function saveUrl(): Promise<void> {
    try {
      await api(`/api/videos/${videoId}/source-url`, { body: { url: externalUrl } });
      toast('Origem externa validada e salva.', 'ok');
      setExternalUrl('');
      setUrlCheck(null);
      onChanged();
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Falha ao salvar a URL.';
      const hint = err instanceof ApiError ? (err.details as { hint?: string } | undefined)?.hint : undefined;
      toast(hint ? `${message} ${hint}` : message, 'error');
    }
  }

  async function detach(): Promise<void> {
    try {
      await api(`/api/videos/${videoId}/detach-source`, { method: 'POST' });
      setFile(null);
      setServerReport(null);
      setCheck({ state: 'idle', message: '' });
      toast('Arquivo removido. Envie outro para publicar novamente.', 'ok');
      onChanged();
    } catch (err) {
      toast(err instanceof ApiError ? err.message : 'Falha ao remover o arquivo.', 'error');
    }
  }

  const report = serverReport ?? asset?.codec_report ?? null;
  const errors = report?.issues.filter((issue) => issue.severity === 'error') ?? [];
  const warnings = report?.issues.filter((issue) => issue.severity === 'warning') ?? [];

  return (
    <div className="grid" style={{ gap: 18 }}>
      <Card
        title="Arquivo de vídeo"
        subtitle="Aceitamos MP4 (H.264 + AAC) e WebM. MOV é aceito com aviso; HEVC/H.265, MKV e ProRes são recusados."
        actions={asset && canWrite ? (
          <button type="button" className="btn ghost small" onClick={detach}>Trocar o arquivo</button>
        ) : undefined}
      >
        {asset && (
          <div className="grid cols-2" style={{ marginBottom: 16 }}>
            <div>
              <p className="stat-label">Origem</p>
              <p style={{ margin: 0 }}>
                <Tag tone={asset.origin_kind === 'external_direct' ? 'info' : 'ok'}>
                  {asset.origin_kind === 'external_direct' ? 'URL direta externa' : 'Storage privado'}
                </Tag>
              </p>
              <p className="card-sub" style={{ wordBreak: 'break-all', marginTop: 6 }}>
                {asset.storage_ref ?? asset.external_url}
              </p>
            </div>
            <div>
              <p className="stat-label">Arquivo</p>
              <p style={{ margin: 0 }}>{asset.original_filename}</p>
              <p className="card-sub">
                {fmtBytes(asset.size_bytes)} · MIME real: <code>{asset.detected_mime || '—'}</code>
                {asset.declared_mime && asset.declared_mime !== asset.detected_mime && (
                  <> · declarado: <code>{asset.declared_mime}</code></>
                )}
              </p>
            </div>
          </div>
        )}

        {report && (
          <div className="grid cols-4" style={{ marginBottom: 14 }}>
            <div className="stat"><p className="stat-label">Container</p><p className="stat-value" style={{ fontSize: 17 }}>{report.container.toUpperCase()}</p></div>
            <div className="stat"><p className="stat-label">Vídeo</p><p className="stat-value" style={{ fontSize: 17 }}>{report.videoCodecLabel ?? '—'}</p></div>
            <div className="stat"><p className="stat-label">Áudio</p><p className="stat-value" style={{ fontSize: 17 }}>{report.audioCodecLabel ?? '—'}</p></div>
            <div className="stat">
              <p className="stat-label">Resolução</p>
              <p className="stat-value" style={{ fontSize: 17 }}>
                {report.width && report.height ? `${report.width}×${report.height}` : '—'}
              </p>
              <p className="stat-meta">{fmtDuration(report.durationSec ?? 0)}</p>
            </div>
          </div>
        )}

        {errors.map((issue) => (
          <div className="alert error" key={issue.code + issue.message} style={{ marginBottom: 10 }}>
            <span>✕</span>
            <div>
              {issue.message}
              {issue.hint && <code>{issue.hint}</code>}
            </div>
          </div>
        ))}
        {warnings.map((issue) => (
          <div className="alert warn" key={issue.code + issue.message} style={{ marginBottom: 10 }}>
            <span>!</span>
            <div>
              {issue.message}
              {issue.hint && <code>{issue.hint}</code>}
            </div>
          </div>
        ))}
        {report && errors.length === 0 && (
          <div className="alert ok" style={{ marginBottom: 10 }}>
            <span>✓</span>
            <div>
              Arquivo validado no servidor: {report.fullMimeType}
              {report.fastStart === false ? ' — sem faststart (o início demora mais).' : '.'}
            </div>
          </div>
        )}

        {status === 'incompatible' && (
          <div className="alert error">
            <span>✕</span>
            <div>
              A publicação está bloqueada enquanto o arquivo for incompatível.
              <code>ffmpeg -i entrada.mov -c:v libx264 -profile:v high -pix_fmt yuv420p -c:a aac -b:a 128k -movflags +faststart saida.mp4</code>
            </div>
          </div>
        )}
      </Card>

      {canWrite && (
        <Card title="Enviar novo arquivo" subtitle="Prévia e validação acontecem antes de qualquer gravação.">
          <div
            className="upload-drop"
            data-drag={dragging ? '1' : '0'}
            onClick={() => inputRef.current?.click()}
            onDragOver={(event) => { event.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={(event) => {
              event.preventDefault();
              setDragging(false);
              pick(event.dataTransfer.files?.[0]);
            }}
          >
            <p style={{ margin: '0 0 6px', fontWeight: 600 }}>Arraste o arquivo aqui ou clique para escolher</p>
            <p style={{ margin: 0, color: 'var(--text-3)', fontSize: 12.5 }}>MP4 H.264/AAC, WebM, MOV ou M4V</p>
            <input
              ref={inputRef} type="file" accept={ACCEPT_ATTR} hidden
              onChange={(event) => pick(event.target.files?.[0])}
            />
          </div>

          {file && (
            <div style={{ marginTop: 16 }}>
              <div className="row" style={{ justifyContent: 'space-between' }}>
                <div>
                  <b>{file.name}</b>
                  <p className="card-sub">
                    {fmtBytes(file.size)} · tipo informado: <code>{file.type || '(vazio)'}</code>
                    {check.canPlayType && <> · canPlayType: <code>{check.canPlayType || 'vazio'}</code></>}
                  </p>
                </div>
                <button type="button" className="btn ghost small" onClick={() => { setFile(null); setCheck({ state: 'idle', message: '' }); }}>
                  Remover
                </button>
              </div>

              {check.state === 'checking' && <div className="alert info"><span className="spinner-inline" /><div>{check.message}</div></div>}
              {check.state === 'failed' && (
                <div className="alert error">
                  <span>✕</span>
                  <div>
                    {check.message}
                    {check.detail && <code>{check.detail}</code>}
                  </div>
                </div>
              )}
              {check.state === 'ok' && (
                <div className="alert ok">
                  <span>✓</span>
                  <div>
                    {check.message} — {check.width}×{check.height}, {fmtDuration(check.duration ?? 0)}.
                  </div>
                </div>
              )}

              {objectUrl && (
                <div style={{ marginTop: 14 }}>
                  <p className="stat-label">Prévia antes de salvar</p>
                  <div className="player-frame">
                    <video ref={previewRef} src={objectUrl} controls playsInline preload="metadata" />
                  </div>
                </div>
              )}

              {progress !== null && (
                <div style={{ marginTop: 14 }}>
                  <div className="row" style={{ justifyContent: 'space-between', fontSize: 12.5 }}>
                    <span>{progress >= 100 ? 'Processando e validando no servidor…' : 'Enviando para o Storage…'}</span>
                    <b>{Math.round(progress)}%</b>
                  </div>
                  <div className="progress-bar"><i style={{ width: `${progress}%` }} /></div>
                </div>
              )}

              <div className="row" style={{ marginTop: 14 }}>
                <button type="button" className="btn" disabled={check.state !== 'ok' || progress !== null} onClick={upload}>
                  Enviar e validar no servidor
                </button>
                {progress !== null && progress < 100 && (
                  <button type="button" className="btn ghost" onClick={() => abortRef.current?.()}>Cancelar</button>
                )}
              </div>
            </div>
          )}
        </Card>
      )}

      {canWrite && (
        <Card
          title="Usar uma URL externa"
          subtitle="Somente links diretos de arquivo. Endereços de página (por exemplo https://seusite.com/) são recusados."
        >
          <div className="row" style={{ alignItems: 'flex-end' }}>
            <div style={{ flex: 1 }}>
              <Field label="URL do arquivo" id="ext" hint="Precisa terminar em .mp4, .webm, .mov ou .m4v — ou responder Content-Type de vídeo.">
                <input id="ext" value={externalUrl} onChange={(e) => setExternalUrl(e.target.value)} placeholder="https://cdn.exemplo.com/video.mp4" />
              </Field>
            </div>
            <button type="button" className="btn secondary" style={{ marginBottom: 14 }} disabled={!externalUrl || urlBusy} onClick={checkUrl}>
              {urlBusy ? <span className="spinner-inline" /> : null} Verificar
            </button>
          </div>

          {urlCheck && (
            <div className={`alert ${urlCheck.ok ? 'ok' : 'error'}`}>
              <span>{urlCheck.ok ? '✓' : '✕'}</span>
              <div>
                {urlCheck.reason}
                {urlCheck.contentType && <code>Content-Type: {urlCheck.contentType} · status {urlCheck.status} · Range: {urlCheck.acceptsRanges ? 'suportado' : 'não suportado'}</code>}
                {urlCheck.hint && <code>{urlCheck.hint}</code>}
              </div>
            </div>
          )}
          {urlCheck?.ok && (
            <button type="button" className="btn" style={{ marginTop: 12 }} onClick={saveUrl}>Usar essa URL como origem</button>
          )}
        </Card>
      )}

      {canWrite && (
        <Card title="Thumbnail" subtitle="Imagem exibida antes do play (JPG, PNG ou GIF, até 5 MB).">
          <input
            ref={thumbInputRef} type="file" accept="image/*" hidden
            onChange={async (event) => {
              const image = event.target.files?.[0];
              if (!image) return;
              try {
                await uploadThumbnail(videoId, image, workspaceId);
                toast('Thumbnail atualizada.', 'ok');
                onChanged();
              } catch (err) {
                toast(err instanceof ApiError ? err.message : 'Falha ao enviar a imagem.', 'error');
              }
            }}
          />
          <button type="button" className="btn secondary" onClick={() => thumbInputRef.current?.click()}>Escolher imagem</button>
        </Card>
      )}
    </div>
  );
}
